import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import EmptyTableRow from '../../reportCards/EmptyTableRow';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import ReportNote from '../../reportCards/ReportNote';
import SortTableCell from '../../reportCards/SortTableCell';
import type { ManualOverrideState } from '../../types';

import BundledExperienceRow from './BundledExperienceRow';
import { BUNDLED_EXPERIENCE_COLUMN_WIDTHS, BUNDLED_EXPERIENCE_NAME_COLUMN_MIN_WIDTH } from './constants';
import type { BundledExperienceRowView } from './types';

interface Props {
  expandedRows: Record<string, boolean>;
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<BundledExperienceRowView>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function BundledExperiencesReportCard({
  expandedRows,
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  return (
    <ReportCard>
      <Stack>
        <ReportNote
          icon={<InfoOutlinedIcon />}
          text={
            <>
              <strong>Bundled Experiences:</strong> auto-approve when validity, currency, and child-age evidence can be
              checked from synced deal-option and opportunity data.
            </>
          }
        />
        <ReportNote
          icon={<WarningAmberIcon />}
          severity="warning"
          text={
            <Stack spacing={0.75}>
              <span>
                <strong>PAX manual check:</strong> all PAX for bundled experiences still need Campaign Manager review.
              </span>
              <span>
                <strong>Blackouts manual check:</strong> always requires Campaign Manager review — there's no dedicated
                per-bundled-experience blackout field, so blackout information is scattered across free text in too many
                places to auto-verify reliably.
              </span>
            </Stack>
          }
        />
      </Stack>

      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <SortTableCell
                sx={{ minWidth: BUNDLED_EXPERIENCE_NAME_COLUMN_MIN_WIDTH }}
                labelSx={{ whiteSpace: 'normal' }}
              >
                Bundled Experience
              </SortTableCell>
              <SortTableCell
                sx={{ width: BUNDLED_EXPERIENCE_COLUMN_WIDTHS.validity, pr: 5 }}
                labelSx={{ whiteSpace: 'normal' }}
              >
                Validity Date
              </SortTableCell>
              <SortTableCell sx={{ width: BUNDLED_EXPERIENCE_COLUMN_WIDTHS.blackouts }}>Blackouts</SortTableCell>
              <SortTableCell sx={{ width: BUNDLED_EXPERIENCE_COLUMN_WIDTHS.currency }}>Currency</SortTableCell>
              <SortTableCell
                sx={{ width: BUNDLED_EXPERIENCE_COLUMN_WIDTHS.childAge }}
                labelSx={{ whiteSpace: 'normal' }}
              >
                Children Age
              </SortTableCell>
              <TableCell sx={{ width: BUNDLED_EXPERIENCE_COLUMN_WIDTHS.action }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={6} label="No bundled experience rows match this view." />
            ) : (
              rows.map((row) => (
                <BundledExperienceRow
                  key={row.id}
                  approved={isRowApproved(row, manualOverrides)}
                  dealsExpanded={!!expandedRows[row.id]}
                  manuallyApproved={isRowManuallyApproved(row, manualOverrides)}
                  row={row}
                  onApprovalChange={onApprovalChange}
                  onToggleDetails={onToggleDetails}
                  onUnapprovalChange={onUnapprovalChange}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <HiddenApprovedFooter
        count={hiddenApprovedCount}
        noun="bundled experience row"
        showAutoApproved={showAutoApproved}
      />
    </ReportCard>
  );
}
